<script>
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { debounce } from '@chatwoot/utils';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LoadingIndicator from 'shared/components/LoadingIndicator.vue';
import FactEditorSkeleton from './FactEditorSkeleton.vue';
import htmlToMarkdownConverter from './HtmlToMarkdownConverter.js';
import markdownToHtmlConverter from './MarkdownToHtmlConverter.js';

export default {
  components: {
    EditorContent,
    NextButton,
    LoadingIndicator,
    FactEditorSkeleton,
  },
  props: {
    factKey: {
      type: String,
      required: true,
    },
    assistant: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      required: true,
    },
    maxLength: {
      type: Number,
      default: 5000,
    },
  },
  emits: ['remove'],
  data() {
    return {
      editor: null,
      debouncedSave: null,
      isAutoSaving: false,
      isLoading: true,
      showDeleteConfirmation: false,
      isEditorFocused: false, // Track editor focus
    };
  },
  computed: {
    fact() {
      return this.$store.getters['facts/getFact'](this.factKey);
    },
    factNumber() {
      // Ensure we have a valid number, fallback to 1 if index is invalid
      const validIndex =
        typeof this.index === 'number' && !Number.isNaN(this.index)
          ? this.index
          : 0;
      return validIndex + 1;
    },
    isFactLoaded() {
      return this.fact && this.fact.content !== undefined;
    },
    characterCount() {
      if (!this.editor) return 0;
      return this.editor.state.doc.textContent.length;
    },
    isOverLimit() {
      return this.characterCount > this.maxLength;
    },
    isNearLimit() {
      return this.characterCount > this.maxLength * 0.8;
    },
    remainingCharacters() {
      return this.maxLength - this.characterCount;
    },
    characterCountText() {
      return `${this.characterCount}`;
    },
  },
  watch: {
    'fact.content'(newContent, oldContent) {
      if (this.editor && newContent !== oldContent) {
        // Only update if not focused (not typing)
        if (!this.isEditorFocused) {
          const htmlContent = markdownToHtmlConverter.convertToHtml(
            newContent || '',
            this.$store
          );
          const currentHtml = this.editor.getHTML();

          // Only update if the content is actually different to avoid infinite loops
          if (htmlContent !== currentHtml) {
            this.editor.commands.setContent(htmlContent);
          }
        }
      }
    },
    isFactLoaded: {
      immediate: true,
      handler(isLoaded) {
        if (isLoaded && !this.editor) {
          this.initializeEditor();
        }
      },
    },
  },
  mounted() {
    // Create debounced save function specific to this instance
    this.debouncedSave = debounce(this.save, 300);

    // Initialize editor if fact is already loaded
    if (this.isFactLoaded) {
      this.initializeEditor();
    }
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
    }
    // Cancel any pending debounced save calls
    if (this.debouncedSave && this.debouncedSave.cancel) {
      this.debouncedSave.cancel();
    }
  },
  methods: {
    initializeEditor() {
      // Convert markdown content to HTML for TipTap editor
      const htmlContent = markdownToHtmlConverter.convertToHtml(
        this.fact.content || '',
        this.$store
      );

      // Destroy previous editor if it exists
      if (this.editor) {
        this.editor.destroy();
      }

      this.editor = new Editor({
        content: htmlContent,
        extensions: [StarterKit, Table, TableRow, TableCell, TableHeader],
        onUpdate: () => {
          this.onContentInput();
        },
        onFocus: () => {
          this.isEditorFocused = true;
        },
        onBlur: () => {
          this.isEditorFocused = false;
        },
      });

      this.isLoading = false;
    },
    onContentInput() {
      // Only save if content actually changed
      if (!this.editor) return;
      this.debouncedSave();
    },
    async save() {
      this.isAutoSaving = true;
      const saveStartTime = Date.now();

      try {
        // Convert HTML content to markdown format before saving
        let htmlContent = this.editor.getHTML();
        // Treat empty content as truly empty
        if (htmlContent === '<p></p>' || htmlContent.trim() === '') {
          htmlContent = '';
        }
        const markdownContent =
          htmlToMarkdownConverter.convertToMarkdown(htmlContent);

        await this.$store.dispatch('facts/updateFact', {
          rookooAgentId: this.assistant.id,
          factKey: this.factKey,
          fact: {
            ...this.fact,
            content: markdownContent,
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to save fact:', error);
      } finally {
        // Ensure the saving indicator shows for at least 800ms
        const elapsed = Date.now() - saveStartTime;
        const minDisplayTime = 800;

        if (elapsed < minDisplayTime) {
          setTimeout(() => {
            this.isAutoSaving = false;
          }, minDisplayTime - elapsed);
        } else {
          this.isAutoSaving = false;
        }
      }
    },
    async remove() {
      this.showDeleteConfirmation = true;
    },
    closeDeletePopup() {
      this.showDeleteConfirmation = false;
    },
    async confirmDelete() {
      await this.$store.dispatch('facts/deleteFact', {
        rookooAgentId: this.assistant.id,
        factKey: this.factKey,
      });
      this.$emit('remove', this.fact);
      this.closeDeletePopup();
    },
  },
};
</script>

<template>
  <!-- Show skeleton while loading -->
  <FactEditorSkeleton v-if="isLoading || !isFactLoaded" />

  <!-- Show actual fact editor when loaded -->
  <div
    v-else
    class="fact-card group mb-6 border border-n-slate-3 dark:border-n-slate-8 rounded-lg p-6 bg-white dark:bg-n-slate-2 shadow-sm"
    data-testid="fact-card"
  >
    <!-- Autosave indicator -->
    <LoadingIndicator
      v-if="isAutoSaving"
      position="fixed"
      placement="bottom-right"
      variant="primary"
      size="md"
      :message="
        $t('ASSISTANT_MGMT.FACTS.SAVING_CHANGES') || 'Saving changes...'
      "
    />

    <!-- Header with fact label and delete button -->
    <div class="flex justify-between items-center mb-4">
      <span
        class="text-sm font-medium text-n-slate-10 bg-n-slate-3 dark:bg-n-slate-7 px-2 py-1 rounded"
      >
        {{
          $t('ASSISTANT_MGMT.FACTS.LABEL', { index: factNumber }) ||
          `Fact ${factNumber}`
        }}
      </span>
      <div
        class="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <NextButton
          icon="i-lucide-x"
          ghost
          sm
          data-testid="fact-delete-button"
          @click="remove"
        />
      </div>
    </div>

    <!-- Editor Content -->
    <div
      class="fact-editor-container bg-white dark:bg-n-slate-1 rounded-lg border border-n-slate-3 dark:border-n-slate-6"
      data-testid="fact-editor-content"
    >
      <EditorContent :editor="editor" class="fact-editor" />

      <!-- Character count indicator positioned in bottom right corner -->
      <div
        class="absolute bottom-2 right-3 bg-white/90 dark:bg-n-slate-11/90 backdrop-blur-sm rounded-md px-2 py-1 text-xs font-medium pointer-events-none z-10 shadow-sm border border-n-slate-3 dark:border-n-slate-8"
      >
        <span
          :class="{
            'text-n-slate-10 dark:text-n-slate-10':
              !isNearLimit && !isOverLimit,
            'text-amber-600 dark:text-amber-400': isNearLimit && !isOverLimit,
            'text-red-500 dark:text-red-400': isOverLimit,
          }"
        >
          {{ characterCountText }}
        </span>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <woot-delete-modal
    v-model:show="showDeleteConfirmation"
    :on-close="closeDeletePopup"
    :on-confirm="confirmDelete"
    :title="$t('ASSISTANT_MGMT.DELETE.FACTS.TITLE')"
    :message="$t('ASSISTANT_MGMT.DELETE.FACTS.MESSAGE')"
    :confirm-text="$t('ASSISTANT_MGMT.DELETE.FACTS.YES')"
    :reject-text="$t('ASSISTANT_MGMT.DELETE.FACTS.NO')"
  />
</template>

<style scoped>
.fact-card {
  transition: all 0.2s ease;
}

.fact-card:hover {
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.fact-editor-container {
  position: relative;
  min-height: 120px;
}
</style>

<style>
.fact-editor .ProseMirror {
  outline: none;
  padding: 12px;
  min-height: 120px;
  width: 100%;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
  line-height: 1.6;
  color: inherit;
}

.fact-editor .ProseMirror:first-child {
  margin-top: 0;
}

.fact-editor .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}

.fact-editor .ProseMirror ul,
.fact-editor .ProseMirror ol {
  padding: 0 1rem;
  margin: 1.25rem 1rem 1.25rem 0.4rem;
}

.fact-editor .ProseMirror ul li p,
.fact-editor .ProseMirror ol li p {
  margin-top: 0.25em;
  margin-bottom: 0.25em;
}

.fact-editor .ProseMirror h1,
.fact-editor .ProseMirror h2,
.fact-editor .ProseMirror h3,
.fact-editor .ProseMirror h4,
.fact-editor .ProseMirror h5,
.fact-editor .ProseMirror h6 {
  line-height: 1.1;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.fact-editor .ProseMirror h1 {
  font-size: 1.4rem;
}

.fact-editor .ProseMirror h2 {
  font-size: 1.2rem;
}

.fact-editor .ProseMirror h3 {
  font-size: 1.1rem;
}

.fact-editor .ProseMirror code {
  background-color: #f3f4f6;
  border-radius: 0.25rem;
  color: #374151;
  font-size: 0.875rem;
  padding: 0.125rem 0.25rem;
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
}

.fact-editor .ProseMirror pre {
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  color: #374151;
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
  margin: 1.5rem 0;
  padding: 1rem;
}

.fact-editor .ProseMirror pre code {
  background: none;
  color: inherit;
  font-size: 0.875rem;
  padding: 0;
  border: none;
}

.fact-editor .ProseMirror blockquote {
  border-left: 3px solid rgba(13, 13, 13, 0.1);
  margin: 1.5rem 0;
  padding-left: 1rem;
}

.fact-editor .ProseMirror hr {
  border: none;
  border-top: 1px solid rgba(13, 13, 13, 0.1);
  margin: 2rem 0;
}

/* Table styles for fact editor */
.fact-editor .ProseMirror table {
  border-collapse: collapse;
  margin: 0;
  overflow: hidden;
  table-layout: fixed;
  width: 100%;
}

.fact-editor .ProseMirror table td,
.fact-editor .ProseMirror table th {
  border: 1px solid var(--b-300);
  box-sizing: border-box;
  min-width: 1em;
  padding: 6px 8px;
  position: relative;
  vertical-align: top;
}

.fact-editor .ProseMirror table td > *,
.fact-editor .ProseMirror table th > * {
  margin-bottom: 0;
}

.fact-editor .ProseMirror table th {
  background-color: var(--b-100);
  font-weight: 700;
  text-align: left;
}

.fact-editor .ProseMirror table .column-resize-handle {
  background-color: var(--w-500);
  bottom: -2px;
  pointer-events: none;
  position: absolute;
  right: -2px;
  top: 0;
  width: 4px;
}

.fact-editor .ProseMirror .tableWrapper {
  margin: 1.5rem 0;
  overflow-x: auto;
}

.fact-editor .ProseMirror.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}
</style>
